import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  AlertTriangle,
  Building2,
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  FileText,
  Mail,
  Phone,
  RefreshCw,
  User,
  X,
  XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  estateApi,
  residentRegistrationApi,
} from "../services/api";

const statusOptions = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

const statusStyles = {
  pending: "badge badge-warning",
  approved: "badge badge-success",
  rejected: "badge badge-danger",
};

const getApiErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  fallbackMessage;

const getId = (item) => item?._id || item?.id;

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const getUnitLabel = (unit) => {
  if (!unit) return "No unit";
  const parts = [unit.unitCode, unit.displayName, unit.block]
    .filter(Boolean)
    .map((part) => String(part).trim());
  return parts.length ? parts.join(" - ") : "No unit";
};

const getEstateName = (registration) =>
  registration?.estate?.name || registration?.estateName || "No estate";

const getUserStatus = (registration) =>
  registration?.user?.accountStatus || registration?.status || "pending";

const DetailRow = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
      {label}
    </p>
    <p className="mt-1 text-sm text-gray-900 break-words">{value || "-"}</p>
  </div>
);

const DocumentLink = ({ label, url }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
      {label}
    </p>
    {url ? (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="mt-1 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
      >
        <FileText className="mr-2 h-4 w-4" />
        Open document
      </a>
    ) : (
      <p className="mt-1 text-sm text-gray-500">Not provided</p>
    )}
  </div>
);

const ResidentRegistrations = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("pending");
  const [estateFilter, setEstateFilter] = useState("");
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [approvalTarget, setApprovalTarget] = useState(null);
  const [dependentLimit, setDependentLimit] = useState("0");
  const [rejectionTarget, setRejectionTarget] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const queryParams = useMemo(() => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (estateFilter) params.estateId = estateFilter;
    return params;
  }, [estateFilter, statusFilter]);

  const {
    data: registrations = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(
    ["residentRegistrations", queryParams],
    () => residentRegistrationApi.getAll(queryParams),
    {
      retry: 1,
    },
  );

  const { data: allRegistrations = [] } = useQuery(
    ["residentRegistrationsSummary", estateFilter],
    () =>
      residentRegistrationApi.getAll(
        estateFilter ? { estateId: estateFilter } : {},
      ),
    {
      retry: 1,
    },
  );

  const { data: estates = [] } = useQuery("estates", estateApi.getAll, {
    retry: 1,
  });

  const counts = useMemo(() => {
    const summary = {
      all: Array.isArray(allRegistrations) ? allRegistrations.length : 0,
      pending: 0,
      approved: 0,
      rejected: 0,
    };

    if (Array.isArray(allRegistrations)) {
      allRegistrations.forEach((registration) => {
        if (summary[registration.status] !== undefined) {
          summary[registration.status] += 1;
        }
      });
    }

    return summary;
  }, [allRegistrations]);

  const approveRegistration = useMutation(
    ({ id, limit }) =>
      residentRegistrationApi.approve(id, {
        dependentLimit: Number(limit),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("users");
        queryClient.invalidateQueries("residentRegistrations");
        queryClient.invalidateQueries(["residentRegistrations"]);
        queryClient.invalidateQueries(["residentRegistrationsSummary"]);
        queryClient.invalidateQueries("residentPropertyUnits");
        toast.success("Resident registration approved");
        setApprovalTarget(null);
        setDependentLimit("0");
      },
      onError: (mutationError) => {
        toast.error(
          getApiErrorMessage(
            mutationError,
            "Failed to approve resident registration",
          ),
        );
      },
    },
  );

  const rejectRegistration = useMutation(
    ({ id, reason }) => residentRegistrationApi.reject(id, { reason }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("residentRegistrations");
        queryClient.invalidateQueries(["residentRegistrations"]);
        queryClient.invalidateQueries(["residentRegistrationsSummary"]);
        toast.success("Resident registration rejected");
        setRejectionTarget(null);
        setRejectionReason("");
      },
      onError: (mutationError) => {
        toast.error(
          getApiErrorMessage(
            mutationError,
            "Failed to reject resident registration",
          ),
        );
      },
    },
  );

  const openApprovalModal = (registration) => {
    setApprovalTarget(registration);
    setDependentLimit(String(registration?.dependentLimit ?? 0));
  };

  const handleApprove = (event) => {
    event.preventDefault();
    const numericLimit = Number(dependentLimit);

    if (!Number.isInteger(numericLimit) || numericLimit < 0 || numericLimit > 4) {
      toast.error("Dependent count must be a whole number between 0 and 4");
      return;
    }

    approveRegistration.mutate({
      id: getId(approvalTarget),
      limit: numericLimit,
    });
  };

  const openRejectionModal = (registration) => {
    setRejectionTarget(registration);
    setRejectionReason("");
  };

  const handleReject = (event) => {
    event.preventDefault();

    if (!rejectionReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }

    rejectRegistration.mutate({
      id: getId(rejectionTarget),
      reason: rejectionReason.trim(),
    });
  };

  const visibleRegistrations = Array.isArray(registrations)
    ? registrations
    : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Resident Approvals
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Review resident registrations submitted from the mobile app or
            created by estate admins.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="card">
          <div className="card-body flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-orange-600">
                {counts.pending}
              </p>
            </div>
            <Clock className="h-8 w-8 text-orange-500" />
          </div>
        </div>
        <div className="card">
          <div className="card-body flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-green-600">
                {counts.approved}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="card">
          <div className="card-body flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-red-600">
                {counts.rejected}
              </p>
            </div>
            <XCircle className="h-8 w-8 text-red-500" />
          </div>
        </div>
        <div className="card">
          <div className="card-body flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-blue-600">
                {counts.all}
              </p>
            </div>
            <User className="h-8 w-8 text-blue-500" />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="form-label">Status</label>
              <select
                className="form-input"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Estate</label>
              <select
                className="form-input"
                value={estateFilter}
                onChange={(event) => setEstateFilter(event.target.value)}
              >
                <option value="">All estates</option>
                {Array.isArray(estates) &&
                  estates.map((estate) => (
                    <option key={estate._id} value={estate._id}>
                      {estate.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              Approving a pending registration activates the resident account,
              assigns the property unit, and sets the dependent count used by
              the mobile app.
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {getApiErrorMessage(error, "Unable to load resident registrations")}
        </div>
      )}

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Registration Queue
            </h2>
            <p className="text-sm text-gray-500">
              {visibleRegistrations.length} registration
              {visibleRegistrations.length === 1 ? "" : "s"} shown
            </p>
          </div>
        </div>

        <div className="card-body p-0">
          {isLoading ? (
            <div className="flex h-48 items-center justify-center">
              <div className="spinner"></div>
            </div>
          ) : visibleRegistrations.length ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Resident</th>
                    <th>Estate / Unit</th>
                    <th>Ownership</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRegistrations.map((registration) => {
                    const isPending = registration.status === "pending";
                    return (
                      <tr key={getId(registration)}>
                        <td>
                          <div className="flex items-start space-x-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                              <User className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {registration.fullName}
                              </p>
                              <p className="flex items-center text-xs text-gray-500">
                                <Mail className="mr-1 h-3 w-3" />
                                {registration.emailAddress}
                              </p>
                              <p className="flex items-center text-xs text-gray-500">
                                <Phone className="mr-1 h-3 w-3" />
                                {registration.mobileNumber}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <p className="flex items-center text-sm font-medium text-gray-900">
                            <Building2 className="mr-2 h-4 w-4 text-gray-400" />
                            {getEstateName(registration)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {getUnitLabel(registration.unit)}
                          </p>
                        </td>
                        <td className="capitalize">
                          {registration.ownershipType || "-"}
                        </td>
                        <td>
                          <span
                            className={
                              statusStyles[registration.status] ||
                              "badge badge-info"
                            }
                          >
                            {registration.status || "unknown"}
                          </span>
                          <p className="mt-1 text-xs text-gray-500">
                            Account: {getUserStatus(registration)}
                          </p>
                        </td>
                        <td>
                          <p className="flex items-center text-sm text-gray-700">
                            <Calendar className="mr-2 h-4 w-4 text-gray-400" />
                            {formatDate(registration.createdAt)}
                          </p>
                          <p className="text-xs text-gray-500 capitalize">
                            Submitted by {registration.submittedByType || "-"}
                          </p>
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="btn btn-secondary text-xs"
                              onClick={() =>
                                setSelectedRegistration(registration)
                              }
                            >
                              <Eye className="mr-1 h-4 w-4" />
                              View
                            </button>
                            {isPending && (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-success text-xs"
                                  onClick={() =>
                                    openApprovalModal(registration)
                                  }
                                >
                                  <CheckCircle className="mr-1 h-4 w-4" />
                                  Approve
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-danger text-xs"
                                  onClick={() =>
                                    openRejectionModal(registration)
                                  }
                                >
                                  <XCircle className="mr-1 h-4 w-4" />
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-16 text-center">
              <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900">
                No resident registrations found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Try another status filter or refresh the queue.
              </p>
            </div>
          )}
        </div>
      </div>

      {selectedRegistration && (
        <div className="modal-overlay">
          <div className="modal max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Registration Details
                </h2>
                <p className="text-sm text-gray-500">
                  {selectedRegistration.fullName} -{" "}
                  {getUnitLabel(selectedRegistration.unit)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedRegistration(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <DetailRow label="Full name" value={selectedRegistration.fullName} />
              <DetailRow
                label="Email"
                value={selectedRegistration.emailAddress}
              />
              <DetailRow
                label="Phone"
                value={selectedRegistration.mobileNumber}
              />
              <DetailRow
                label="Estate"
                value={getEstateName(selectedRegistration)}
              />
              <DetailRow
                label="Property unit"
                value={getUnitLabel(selectedRegistration.unit)}
              />
              <DetailRow
                label="Ownership type"
                value={selectedRegistration.ownershipType}
              />
              <DetailRow
                label="Date of birth"
                value={formatDate(selectedRegistration.dateOfBirth)}
              />
              <DetailRow label="Gender" value={selectedRegistration.gender} />
              <DetailRow
                label="Move-in date"
                value={formatDate(selectedRegistration.moveInDate)}
              />
              <DetailRow
                label="Government ID"
                value={selectedRegistration.governmentIdNumber}
              />
              <DetailRow
                label="Access card number"
                value={selectedRegistration.cardNo}
              />
              <DetailRow
                label="Dependent count"
                value={
                  selectedRegistration.dependentLimit !== undefined
                    ? selectedRegistration.dependentLimit
                    : "Not set"
                }
              />
              <DetailRow
                label="Submitted"
                value={formatDate(selectedRegistration.createdAt)}
              />
              <DetailRow
                label="Emergency contact"
                value={selectedRegistration.emergencyContact?.name}
              />
              <DetailRow
                label="Emergency phone"
                value={selectedRegistration.emergencyContact?.phone}
              />
              <DetailRow
                label="Occupation"
                value={selectedRegistration.occupation}
              />
              <DetailRow
                label="Employer"
                value={selectedRegistration.employer}
              />
              <DetailRow
                label="Reviewed by"
                value={
                  selectedRegistration.reviewedBy?.name ||
                  selectedRegistration.reviewedBy?.username
                }
              />
              <DetailRow
                label="Reviewed at"
                value={formatDate(selectedRegistration.reviewedAt)}
              />
            </div>

            {Array.isArray(selectedRegistration.vehicleDetails) &&
              selectedRegistration.vehicleDetails.length > 0 && (
                <div className="mt-6">
                  <h3 className="mb-3 font-semibold text-gray-900">
                    Vehicle Details
                  </h3>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {selectedRegistration.vehicleDetails.map((vehicle, index) => (
                      <div
                        key={`${vehicle.plateNumber || "vehicle"}-${index}`}
                        className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm"
                      >
                        <p className="font-medium text-gray-900">
                          {vehicle.plateNumber || "No plate number"}
                        </p>
                        <p className="text-gray-600">
                          {[vehicle.make, vehicle.model, vehicle.color]
                            .filter(Boolean)
                            .join(" / ") || "No vehicle details"}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
              <DocumentLink
                label="Profile photo"
                url={selectedRegistration.profilePhotoUrl}
              />
              <DocumentLink
                label="Proof of residency"
                url={selectedRegistration.proofOfResidencyDocumentUrl}
              />
              <DocumentLink
                label="Government ID document"
                url={selectedRegistration.governmentIdDocumentUrl}
              />
            </div>

            {selectedRegistration.additionalNotes && (
              <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Additional notes
                </p>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedRegistration.additionalNotes}
                </p>
              </div>
            )}

            {selectedRegistration.rejectionReason && (
              <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-red-700">
                  Rejection reason
                </p>
                <p className="mt-1 text-sm text-red-900">
                  {selectedRegistration.rejectionReason}
                </p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3 border-t pt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setSelectedRegistration(null)}
              >
                Close
              </button>
              {selectedRegistration.status === "pending" && (
                <>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => {
                      setSelectedRegistration(null);
                      openRejectionModal(selectedRegistration);
                    }}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => {
                      setSelectedRegistration(null);
                      openApprovalModal(selectedRegistration);
                    }}
                  >
                    Approve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {approvalTarget && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Approve Registration
                </h2>
                <p className="text-sm text-gray-500">
                  {approvalTarget.fullName} - {getUnitLabel(approvalTarget.unit)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setApprovalTarget(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleApprove} className="space-y-4">
              <div className="rounded-lg border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-900">
                Approval will create the active resident profile, activate the
                user account, and mark the property unit as occupied.
              </div>

              <div>
                <label className="form-label">Dependent Count</label>
                <input
                  type="number"
                  min="0"
                  max="4"
                  step="1"
                  className="form-input"
                  value={dependentLimit}
                  onChange={(event) => setDependentLimit(event.target.value)}
                  required
                />
                <p className="mt-2 text-xs text-gray-500">
                  This controls how many dependents the resident can add in the
                  mobile app.
                </p>
              </div>

              <div className="flex justify-end gap-3 border-t pt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setApprovalTarget(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={approveRegistration.isLoading}
                >
                  {approveRegistration.isLoading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Approving...
                    </>
                  ) : (
                    "Approve Resident"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {rejectionTarget && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Reject Registration
                </h2>
                <p className="text-sm text-gray-500">
                  {rejectionTarget.fullName} - {getUnitLabel(rejectionTarget.unit)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setRejectionTarget(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleReject} className="space-y-4">
              <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-900">
                The resident will see this reason and can resubmit after
                correcting the issue.
              </div>

              <div>
                <label className="form-label">Rejection Reason</label>
                <textarea
                  className="form-input min-h-[120px]"
                  value={rejectionReason}
                  onChange={(event) => setRejectionReason(event.target.value)}
                  placeholder="Example: Uploaded document does not match the selected property unit."
                  required
                />
              </div>

              <div className="flex justify-end gap-3 border-t pt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setRejectionTarget(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-danger"
                  disabled={rejectRegistration.isLoading}
                >
                  {rejectRegistration.isLoading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Rejecting...
                    </>
                  ) : (
                    "Reject Registration"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResidentRegistrations;
